/**
 * 참여자용 "문의하기" 카드
 *
 * 운영자(고객사) 문의처를 눈에 띄는 카드로 노출한다.
 * 기존에는 최하단 푸터에 있어 발견성이 낮았기 때문에,
 * 신청/미션 제출/내 신청 페이지의 본문 안에 카드로 배치한다.
 *
 * 전화 → tel:, 카카오 → 새 탭, 이메일 → mailto: 로 연결한다.
 * 문의처가 미등록(모든 필드 비어있음)이면 null을 반환해 아무것도 렌더하지 않는다.
 */
import { Mail, MessageCircle, Phone } from 'lucide-react'
import type { SeedingContact } from '@/lib/seedingPublicApi'
import { cn } from '@/lib/utils'

interface ContactCardProps {
  // 운영자(고객사) 문의처 — 미등록이면 카드 자체를 숨긴다.
  contact?: SeedingContact | null
  // 쇼핑몰(브랜드) 이름 — 제목을 "{shopName}에 문의하기"로 표기한다.
  shopName?: string | null
  className?: string
}

export function ContactCard({
  contact,
  shopName,
  className,
}: ContactCardProps) {
  // 3개 필드 중 하나라도 값이 있어야 카드를 노출한다.
  const hasContact = !!(
    contact &&
    (contact.phone || contact.kakao_url || contact.email)
  )
  if (!hasContact) return null

  // 브랜드명이 있으면 "{브랜드}에 문의하기", 없으면 "문의하기"
  const title = shopName ? `${shopName}에 문의하기` : '문의하기'

  // 문의 수단별 링크 공통 스타일 (터치 타겟 44px, 탭 가능한 행 형태)
  const linkClass =
    'flex items-center gap-3 min-h-[44px] px-3.5 rounded-lg border border-border bg-surface text-sm font-medium text-on-surface hover:bg-surface-low active:bg-surface-low transition-colors'

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-surface-lowest p-5',
        className,
      )}
    >
      {/* 제목 */}
      <h2 className="text-base font-semibold text-on-surface">{title}</h2>

      {/* 문의 수단별 링크 */}
      <div className="mt-4 flex flex-col gap-2">
        {/* 전화 → tel: */}
        {contact?.phone && (
          <a href={`tel:${contact.phone}`} className={linkClass}>
            <Phone
              className="w-4 h-4 flex-shrink-0 text-on-surface-variant"
              aria-hidden
            />
            <span>{contact.phone}</span>
          </a>
        )}

        {/* 카카오톡 → 새 탭 */}
        {contact?.kakao_url && (
          <a
            href={contact.kakao_url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            <MessageCircle
              className="w-4 h-4 flex-shrink-0 text-on-surface-variant"
              aria-hidden
            />
            <span>카카오톡으로 문의하기</span>
          </a>
        )}

        {/* 이메일 → mailto: */}
        {contact?.email && (
          <a
            href={`mailto:${contact.email}`}
            className={cn(linkClass, 'break-all')}
          >
            <Mail
              className="w-4 h-4 flex-shrink-0 text-on-surface-variant"
              aria-hidden
            />
            <span>{contact.email}</span>
          </a>
        )}
      </div>
    </div>
  )
}
